import { fetchKeywordCounts } from "api/apis";
import { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Dropdown,
  Button,
  Spinner,
  Form,
} from "react-bootstrap";

interface SearchItem {
  _id: string;
  keyword: string;
  user?: {
    _id: string;
    email: string;
    username?: string;
    firstName?: string;
  } | null;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface KeywordCount {
  keyword: string;
  searchCount: number;
  uniqueUserCount: number;
  lastSearched: string;
}

interface ApiResponse {
  type: "keyword-counts" | "search-history";
  period: string;
  count: number;
  totalCount?: number;
  currentPage?: number;
  totalPages?: number;
  keywordCounts?: KeywordCount[];
  searches?: SearchItem[];
}

interface UserOption {
  _id: string;
  email: string;
}

const SearchHistory = () => {
  const [searchData, setSearchData] = useState<SearchItem[]>([]);
  const [keywordCounts, setKeywordCounts] = useState<KeywordCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"highToLow" | "lowToHigh">(
    "highToLow"
  );
  const [emailOptions, setEmailOptions] = useState<UserOption[]>([]);
  const [emailSearch, setEmailSearch] = useState("");
  const [selectedEmail, setSelectedEmail] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    period: "",
    keyword: "",
    startDate: "",
    endDate: "",
    userSearch: "",
    viewMode: "counts" as "counts" | "history",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
  });

  useEffect(() => {
    fetchData();
  }, [filters, pagination.page, pagination.limit]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const allowedPeriods = [
        "daily",
        "weekly",
        "monthly",
        "quarterly",
        "halfyearly",
        "yearly",
      ] as const;
      type AllowedPeriod = (typeof allowedPeriods)[number];

      const periodValue: AllowedPeriod | undefined = allowedPeriods.includes(
        filters.period as AllowedPeriod
      )
        ? (filters.period as AllowedPeriod)
        : undefined;

      const params = {
        history: filters.viewMode === "history",
        period: periodValue,
        keyword: filters.keyword || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        userSearch: filters.userSearch || undefined,
        page: filters.viewMode === "history" ? pagination.page : undefined,
        limit: filters.viewMode === "history" ? pagination.limit : undefined,
      };

      const response = await fetchKeywordCounts(params);

      if (response.type === "keyword-counts" && response.keywordCounts) {
        setKeywordCounts(response.keywordCounts);
      } else if (response.type === "search-history" && response.searches) {
        setSearchData(response.searches);
        updateEmailOptions(response.searches);
      }
    } catch (err) {
      setError("Failed to fetch search data");
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const updateEmailOptions = (searches: SearchItem[]) => {
    const userOptions = searches
      .filter((item) => item.user)
      .map((item) => ({
        _id: item.user?._id || "",
        email: item.user?.email || "",
      }))
      .filter(
        (user, index, self) =>
          self.findIndex((u) => u._id === user._id) === index
      );
    setEmailOptions(userOptions);
  };

  const filteredEmails = emailOptions.filter((user) =>
    user.email.toLowerCase().includes(emailSearch.toLowerCase())
  );

  const handleEmailSelect = (user: UserOption) => {
    setSelectedEmail(user.email);
    setEmailSearch(user.email);
    handleFilterChange("userSearch", user._id);
  };

  const handleExport = async () => {
    try {
      setLoading(true);

      const allowedPeriods = [
        "daily",
        "weekly",
        "monthly",
        "quarterly",
        "halfyearly",
        "yearly",
      ] as const;
      type AllowedPeriod = (typeof allowedPeriods)[number];

      const periodValue: AllowedPeriod | undefined = allowedPeriods.includes(
        filters.period as AllowedPeriod
      )
        ? (filters.period as AllowedPeriod)
        : undefined;

      const params = {
        export: true,
        history: filters.viewMode === "history",
        period: periodValue,
        keyword: filters.keyword || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        userSearch: filters.userSearch || undefined,
      };

      const response = await fetchKeywordCounts(params);

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const fileName = `search-${
        filters.viewMode === "history" ? "history" : "analytics"
      }-${periodValue || "all"}.csv`;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Export error:", error);
      setError("Failed to export data");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (name: keyof typeof filters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (name !== "viewMode") {
      setPagination((prev) => ({ ...prev, page: 1 }));
    }
  };

  const toggleViewMode = () => {
    setFilters((prev) => ({
      ...prev,
      viewMode: prev.viewMode === "counts" ? "history" : "counts",
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      period: "",
      keyword: "",
      startDate: "",
      endDate: "",
      userSearch: "",
      viewMode: filters.viewMode,
    });
    setSelectedEmail("");
    setEmailSearch("");
    setPagination({ page: 1, limit: 50 });
  };

  const sortedCounts = [...keywordCounts].sort((a, b) =>
    sortOrder === "highToLow"
      ? b.searchCount - a.searchCount
      : a.searchCount - b.searchCount
  );

  const sortedHistory = [...searchData].sort((a, b) => {
    const dateA = new Date(a.createdAt).getTime();
    const dateB = new Date(b.createdAt).getTime();
    return sortOrder === "highToLow" ? dateB - dateA : dateA - dateB;
  });

  return (
    <div className="page-content">
      <Card>
        <Card.Header className="border-bottom-dashed">
          <Row className="g-3 align-items-center">
            <Col xxl={4} lg={6}>
              <h5 className="mb-0">Search History Analytics</h5>
            </Col>

            <Col xxl={2} lg={3}>
              <Button
                variant="outline-secondary"
                className="w-100"
                onClick={() => setShowFilters(!showFilters)}
              >
                <i className="ri-filter-line me-1"></i>
                {showFilters ? "Hide Filters" : "Show Filters"}
              </Button>
            </Col>

            <Col xxl={2} lg={3}>
              <Button
                variant="soft-info"
                className="w-100"
                onClick={toggleViewMode}
              >
                {filters.viewMode === "counts" ? "Show History" : "Show Counts"}
              </Button>
            </Col>

            <Col xxl={2} lg={3}>
              <Button
                variant="soft-primary"
                className="w-100"
                onClick={handleExport}
                disabled={loading}
              >
                {loading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <>
                    <i className="ri-download-2-line align-bottom me-1"></i>{" "}
                    Export CSV
                  </>
                )}
              </Button>
            </Col>

            <Col xxl={2} lg={3}>
              <Dropdown>
                <Dropdown.Toggle variant="soft-secondary" className="w-100">
                  <i
                    className={`ri-sort-${
                      sortOrder === "highToLow" ? "desc" : "asc"
                    }-align-bottom me-1`}
                  ></i>
                  {sortOrder === "highToLow" ? "High-Low" : "Low-High"}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => setSortOrder("highToLow")}>
                    <i className="ri-sort-desc align-bottom me-1"></i> High to
                    Low
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => setSortOrder("lowToHigh")}>
                    <i className="ri-sort-asc align-bottom me-1"></i> Low to
                    High
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Col>
          </Row>

          {showFilters && (
            <Row className="g-3 mt-3 align-items-center">
              <Col xxl={2} lg={4}>
                <Form.Select
                  value={filters.period}
                  onChange={(e) => handleFilterChange("period", e.target.value)}
                >
                  <option value="">All Time</option>
                  <option value="daily">Today</option>
                  <option value="weekly">This Week</option>
                  <option value="monthly">This Month</option>
                  <option value="quarterly">This Quarter</option>
                  <option value="yearly">This Year</option>
                </Form.Select>
              </Col>

              <Col xxl={2} lg={4}>
                <Form.Control
                  type="text"
                  placeholder="Filter by keyword..."
                  value={filters.keyword}
                  onChange={(e) =>
                    handleFilterChange("keyword", e.target.value)
                  }
                />
              </Col>

              <Col xxl={2} lg={4}>
                <Form.Control
                  type="date"
                  placeholder="From date"
                  value={filters.startDate}
                  onChange={(e) =>
                    handleFilterChange("startDate", e.target.value)
                  }
                />
              </Col>

              <Col xxl={2} lg={4}>
                <Form.Control
                  type="date"
                  placeholder="To date"
                  value={filters.endDate}
                  onChange={(e) =>
                    handleFilterChange("endDate", e.target.value)
                  }
                />
              </Col>

              <Col xxl={2} lg={4}>
                <Form.Group>
                  <Dropdown>
                    <Dropdown.Toggle variant="light" id="dropdown-email-filter">
                      {selectedEmail || "Filter by User"}
                    </Dropdown.Toggle>
                    <Dropdown.Menu style={{ width: "100%" }}>
                      <div className="px-3 py-2">
                        <Form.Control
                          type="text"
                          placeholder="Search users..."
                          value={emailSearch}
                          onChange={(e) => setEmailSearch(e.target.value)}
                        />
                      </div>
                      <Dropdown.Divider />
                      {filteredEmails.length > 0 ? (
                        filteredEmails.map((user, index) => (
                          <Dropdown.Item
                            key={index}
                            onClick={() => handleEmailSelect(user)}
                          >
                            {user.email}
                          </Dropdown.Item>
                        ))
                      ) : (
                        <Dropdown.Item disabled>
                          No matching users found
                        </Dropdown.Item>
                      )}
                    </Dropdown.Menu>
                  </Dropdown>
                </Form.Group>
              </Col>

              <Col xxl={2} lg={4}>
                <Button
                  variant="outline-danger"
                  className="w-100"
                  onClick={clearFilters}
                >
                  <i className="ri-close-line me-1"></i> Clear
                </Button>
              </Col>
            </Row>
          )}
        </Card.Header>

        <Card.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading search analytics...</p>
            </div>
          ) : error ? (
            <div className="text-center py-4 text-danger">
              <i className="ri-error-warning-line fs-3"></i>
              <p className="mt-2">{error}</p>
              <Button variant="outline-primary" onClick={fetchData}>
                <i className="ri-refresh-line me-1"></i> Retry
              </Button>
            </div>
          ) : filters.viewMode === "counts" ? (
            sortedCounts.length === 0 ? (
              <div className="text-center py-4">
                <i className="ri-search-line fs-3 text-muted"></i>
                <p className="mt-2">No search data found</p>
              </div>
            ) : (
              <Row className="g-4">
                {sortedCounts.map((item, index) => (
                  <Col key={index} xxl={3} lg={4} md={6}>
                    <Card className="mb-0 h-100">
                      <Card.Body>
                        <div className="d-flex align-items-center h-100">
                          <div className="flex-grow-1">
                            <h6
                              className="fs-15 mb-1 text-truncate"
                              title={item.keyword}
                            >
                              {item.keyword}
                            </h6>
                            <div className="d-flex align-items-center flex-wrap gap-2 mt-2">
                              <span
                                className={`badge bg-${getCountBadgeColor(
                                  item.searchCount
                                )}-subtle text-${getCountBadgeColor(
                                  item.searchCount
                                )} p-2`}
                              >
                                <i className="ri-search-line align-bottom me-1"></i>{" "}
                                {item.searchCount} searches
                              </span>
                              {item.uniqueUserCount > 0 && (
                                <span className="badge bg-info-subtle text-info p-2">
                                  <i className="ri-user-line align-bottom me-1"></i>{" "}
                                  {item.uniqueUserCount} users
                                </span>
                              )}
                            </div>
                            <p className="text-muted mb-0 mt-2">
                              <small>
                                <i className="ri-time-line align-middle me-1"></i>{" "}
                                Last searched:{" "}
                                {new Date(item.lastSearched).toLocaleString()}
                              </small>
                            </p>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            )
          ) : sortedHistory.length === 0 ? (
            <div className="text-center py-4">
              <i className="ri-search-line fs-3 text-muted"></i>
              <p className="mt-2">No search history found</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-borderless table-centered align-middle table-nowrap mb-0">
                <thead className="text-muted table-light">
                  <tr>
                    <th scope="col">Keyword</th>
                    <th scope="col">User</th>
                    <th scope="col">Searched At</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedHistory.map((item, idx) => (
                    <tr key={idx}>
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="flex-shrink-0 me-2">
                            <i className="ri-search-line fs-16 text-primary"></i>
                          </div>
                          <div className="flex-grow-1">{item.keyword}</div>
                        </div>
                      </td>
                      <td>
                        {item.user ? (
                          <>
                            <span className="d-block fw-medium">
                              {item.user.firstName ||
                                item.user.username ||
                                item.user.email}
                            </span>
                            <small className="text-muted">
                              {item.user.email}
                            </small>
                          </>
                        ) : (
                          <span className="text-muted">Anonymous</span>
                        )}
                      </td>
                      <td>{new Date(item.createdAt).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>

        {filters.viewMode === "history" && searchData.length > 0 && (
          <Card.Footer className="border-top-dashed">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                Showing {searchData.length} of {pagination.limit} entries
              </div>
              <div className="d-flex gap-2">
                <Button
                  variant="soft-primary"
                  size="sm"
                  disabled={pagination.page === 1}
                  onClick={() =>
                    setPagination((prev) => ({ ...prev, page: prev.page - 1 }))
                  }
                >
                  Previous
                </Button>
                <Button
                  variant="soft-primary"
                  size="sm"
                  onClick={() =>
                    setPagination((prev) => ({ ...prev, page: prev.page + 1 }))
                  }
                >
                  Next
                </Button>
              </div>
            </div>
          </Card.Footer>
        )}
      </Card>
    </div>
  );
};

const getCountBadgeColor = (count: number) => {
  if (count >= 10) return "danger";
  if (count >= 5) return "warning";
  if (count >= 2) return "primary";
  return "secondary";
};

export default SearchHistory;
